import { DOMAttributes, memo, ReactNode, useEffect, useLayoutEffect } from 'react'
import { autoUpdate, ComputePositionConfig, flip, offset, ReferenceType, useFloating } from '@floating-ui/react-dom'
import cn from 'classnames'
import noop from 'lodash/noop'

import { Portal } from '../Portal'
import { defaultGetOptionId, defaultGetOptionName, defaultLoadingMessage } from './helpers/defaultValues'
import { IOptionsScrollBlockProps, OptionsScrollBlock } from './OptionsScrollBlock'
import { BaseOptionType } from './types'

import s from './BaseSelect.module.less'

export interface IOptionListProps<Option extends BaseOptionType>
  extends Omit<IOptionsScrollBlockProps<Option>, 'scheduleUpdate' | 'onChange'> {
  /** Элемент, относительно которого нужно отрисовать список для десктопа */
  desktopParentElement: ReferenceType | null
  /** Коллбек, выполняемый при выборе опции в десктопном виде */
  onChangeForDesktop: (option: Option) => void
  /** Расположение списка относительно parentElement для десктопа */
  desktopPlacement?: ComputePositionConfig['placement']
  /** Подвал для списка опций мобильного вида */
  mobileOptionListFooter?: ReactNode
  /** Шапка для списка опций мобильного вида */
  mobileOptionListHeader?: ReactNode
  onMouseEnter?: DOMAttributes<HTMLDivElement>['onMouseEnter']
  onMouseLeave?: DOMAttributes<HTMLDivElement>['onMouseLeave']
  /** Элемент для отрисовки футера в выпадающем списке */
  optionListFooter?: ReactNode
  /** Элемент для отрисовки шапки в выпадающем списке */
  optionListHeader?: ReactNode
  testId?: string
}

const OptionListNoMemo = <Option extends BaseOptionType>({
  selectWidth,
  hasAutoOptionListWidth = false,
  maxOptionListWidth,
  minOptionListWidth = selectWidth,
  classNames = {},
  value,
  options = [],
  optionHoverIndex = -1,
  maxOptionListHeight,
  isLoading = false,
  showLoadingMessage = isLoading,
  loadingMessage = defaultLoadingMessage,
  onSetOptionHoverIndex = noop,
  onChangeForDesktop = noop,
  onScrollOptionList = noop,
  getOptionId = defaultGetOptionId,
  getOptionName = defaultGetOptionName,
  optionListHeader,
  optionListFooter,
  CustomOptionComponent,
  hoverOptionRef,
  desktopParentElement,
  desktopPlacement = 'bottom-start',
  onMouseEnter,
  onMouseLeave,
  testId
}: IOptionListProps<Option>) => {
  const generalProps = {
    value,
    options,
    selectWidth,
    optionHoverIndex,
    isLoading,
    getOptionId,
    getOptionName,
    onSetOptionHoverIndex,
    onScrollOptionList,
    CustomOptionComponent
  }

  const { x, y, refs, strategy, update } = useFloating({
    placement: desktopPlacement,
    middleware: [offset(5), flip()],
    whileElementsMounted: autoUpdate
  })

  useLayoutEffect(() => {
    if (desktopParentElement) {
      refs.setReference(desktopParentElement)
    }
  }, [desktopParentElement, refs])

  useEffect(() => {
    update && update()
  }, [options.length, update])

  return (
    <Portal>
      <div
        ref={refs.setFloating}
        className={cn(s.optionListWrapper, classNames.optionListWrapper)}
        data-name="selectPopup"
        data-test-id={testId}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          top: y ?? '',
          left: x ?? '',
          position: strategy,
          width: hasAutoOptionListWidth ? 'auto' : selectWidth,
          minWidth: minOptionListWidth,
          maxWidth: maxOptionListWidth
        }}
      >
        {optionListHeader}

        <OptionsScrollBlock
          {...generalProps}
          classNames={classNames}
          hoverOptionRef={hoverOptionRef}
          loadingMessage={loadingMessage}
          maxOptionListHeight={maxOptionListHeight}
          onChange={onChangeForDesktop}
          showLoadingMessage={showLoadingMessage}
        />

        {optionListFooter}
      </div>
    </Portal>
  )
}

export const OptionList = memo(OptionListNoMemo) as typeof OptionListNoMemo
